import React, {useState, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {supabase} from '@/services/supabase';
import {LoadingSpinner, Button, Input} from '@/components/ui';
import toast from 'react-hot-toast';

interface AcceptInvitationProps {
    userType?: 'host' | 'admin';
    tableName?: string;
    statusField?: string;
    redirectPath?: string;
}

const AcceptInvitation: React.FC<AcceptInvitationProps> = ({
                                                               userType = 'host',
                                                               tableName = 'game_hosts',
                                                               statusField = 'invitation_status',
                                                               redirectPath = '/host-dashboard'
                                                           }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [userEmail, setUserEmail] = useState<string>('');
    const [accessToken, setAccessToken] = useState<string>('');

    // ADD these new state variables for secure token flow:
    const [isSecureTokenFlow, setIsSecureTokenFlow] = useState<boolean>(false);
    const [invitationData, setInvitationData] = useState<any>(null);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [settingPassword, setSettingPassword] = useState<boolean>(false);

    useEffect(() => {
        handleInvitation();
    }, []);

    // UPDATE the handleInvitation function to handle both flows:
    const handleInvitation = async () => {
        console.log(`🔍 ${userType} invitation - Starting invitation handling`);
        console.log('🔍 Current URL:', window.location.href);

        try {
            // First, check for secure custom token (new flow)
            const customToken = searchParams.get('token');

            if (customToken) {
                console.log('🔐 Using secure custom token flow');
                setIsSecureTokenFlow(true);
                await handleSecureTokenFlow(customToken);
                return;
            }

            // Fall back to magic link flow (existing flow)
            console.log('🔗 Using magic link flow');
            await handleMagicLinkFlow();

        } catch (error: any) {
            console.error(`${userType} invitation handling error:`, error);
            toast.error(error.message || 'Failed to process invitation');
            navigate('/login');
        }
    };

    // ADD new function for secure token flow:
    const handleSecureTokenFlow = async (token: string): Promise<void> => {
        try {
            console.log('🔍 Validating custom token...');

            // Validate token and get invitation data
            let query = supabase
                .from(tableName)
                .select('*')
                .eq('custom_invitation_token', token);

            // Only apply deleted_at filter for game_hosts table (admin_users doesn't have this column)
            if (tableName === 'game_hosts') {
                query = query.is('deleted_at', null);
            }

            const {data: invitation, error} = await query.maybeSingle();

            if (error) {
                console.error('❌ Database error during token validation:', error);
                toast.error('Failed to validate invitation link. Please try again.');
                navigate('/login');
                return;
            }

            if (!invitation) {
                console.error('❌ No invitation found with token:', token);
                toast.error('Invalid or expired invitation link. Please request a new invitation.');
                navigate('/login');
                return;
            }

            // Check status based on user type
            if (userType === 'admin' && invitation.status !== 'inactive') {
                toast.error('This invitation has already been used or is not valid.');
                navigate('/login');
                return;
            }

            if (userType === 'host' && invitation.invitation_status === 'accepted') {
                // Check if user is already signed in
                const {data: {user}} = await supabase.auth.getUser();
                if (user?.email === invitation.email) {
                    toast.success('You are already signed in.');
                    navigate(redirectPath);
                    return;
                } else {
                    toast.error('Invitation already accepted. Please sign in with your password.');
                    navigate('/login');
                    return;
                }
            }

            console.log('✅ Valid invitation found:', invitation.email);
            setInvitationData(invitation);
            setUserEmail(invitation.email);

        } catch (error: any) {
            console.error('❌ Secure token validation error:', error);
            toast.error('Failed to validate invitation');
            navigate('/login');
        }
    };

    // UPDATE existing magic link flow (preserve existing logic):
    const handleMagicLinkFlow = async (): Promise<void> => {
        // Get access token from URL params or hash
        let token = searchParams.get('access_token');
        console.log('🔍 Token from search params:', token);

        if (!token) {
            // Try to get from hash (alternative method)
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            token = hashParams.get('access_token');
            console.log('🔍 Token from hash:', token);
        }

        if (!token) {
            console.log('❌ No token found in URL');
            toast.error('Invalid invitation link. Please use the link from your invitation email.');
            navigate('/login');
            return;
        }

        console.log('Found access token, verifying...');
        setAccessToken(token);

        // Get user info from the token to find their email
        const {data: {user}, error: userError} = await supabase.auth.getUser(token);

        if (userError || !user?.email) {
            console.error('Token verification failed:', userError);
            toast.error('Invalid or expired invitation token. Please request a new invitation.');
            navigate('/login');
            return;
        }

        console.log(`Token verified for ${userType}:`, user.email);
        setUserEmail(user.email);

        // Find the user record by email (existing logic)
        await findUserByEmailForInvite(user.email);
    };

    const handlePasswordSetup = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        if (!invitationData) return;

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        setSettingPassword(true);

        try {
            console.log(`🔑 Setting up password for ${userType}:`, invitationData.email);

            // Call edge function to set password (uses service role)
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/setup-invitation-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({
                    token: searchParams.get('token'),
                    password: password,
                    user_type: userType
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to set password');
            }

            console.log('✅ Password set via edge function');

            // Now sign them in with their new password
            const {data: authData, error: signInError} = await supabase.auth.signInWithPassword({
                email: invitationData.email,
                password: password
            });

            if (signInError) {
                console.error('❌ Sign in error after password setup:', signInError);
                throw new Error('Password was set but sign-in failed. Please try signing in manually.');
            }

            console.log(`✅ ${userType} signed in successfully:`, authData.user.email);

            toast.success('Password set successfully! Welcome to the platform.');
            navigate(redirectPath, {replace: true});

        } catch (error: any) {
            console.error(`❌ Password setup error:`, error);
            toast.error(error.message || 'Failed to set password');
        } finally {
            setSettingPassword(false);
        }
    };

    // KEEP your existing findUserByEmailForInvite function unchanged
    const findUserByEmailForInvite = async (email: string): Promise<void> => {
        try {
            console.log(`🔍 Looking for ${userType} record with email:`, email);

            let query = supabase
                .from(tableName)
                .select('*')
                .eq('email', email)
                .is('deleted_at', null);

            if (userType === 'host') {
                query = query.eq(statusField, 'sent');
            } else {
                query = query.eq('password_reset_required', true);
            }

            const {data: userData, error} = await query.single();

            if (error) {
                console.log(`❌ Error finding ${userType}:`, error);

                let acceptedQuery = supabase
                    .from(tableName)
                    .select('*')
                    .eq('email', email);

                if (userType === 'host') {
                    acceptedQuery = acceptedQuery.eq(statusField, 'accepted');
                } else {
                    acceptedQuery = acceptedQuery.eq('status', 'active').eq('password_reset_required', false);
                }

                const {data: acceptedUser, error: acceptedError} = await acceptedQuery.single();

                if (!acceptedError && acceptedUser) {
                    const message = userType === 'host'
                        ? 'Invitation already accepted. Please sign in with your password.'
                        : 'Account is already active. Please sign in with your password.';
                    toast.success(message);
                    navigate('/login');
                    return;
                }

                throw new Error(`${userType} record not found. Please contact your administrator.`);
            }

            if (!userData) {
                throw new Error(`${userType} record not found. Please contact your administrator.`);
            }

            console.log(`✅ Found ${userType} record:`, userData.email);

            if (userType === 'host' && userData.invitation_status === 'sent') {
                console.log('📝 Updating host invitation status to accepted...');
                const {error: updateError} = await supabase
                    .from(tableName)
                    .update({
                        invitation_status: 'accepted',
                        updated_at: new Date().toISOString()
                    })
                    .eq('email', email);

                if (updateError) {
                    console.error('❌ Failed to update invitation status:', updateError);
                    throw new Error('Failed to accept invitation');
                }

                console.log('✅ Host invitation status updated to accepted');
            }

            const {data: {session: existingSession}} = await supabase.auth.getSession();
            if (existingSession) {
                console.log('✅ Session already established, proceeding to redirect');
                navigate(redirectPath, {replace: true});
                return;
            }

            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const refreshToken = hashParams.get('refresh_token') || '';

            const {error: sessionError} = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            });

            if (sessionError) {
                console.error('Session establishment error:', sessionError);
                throw new Error('Failed to establish session');
            }

            toast.success('Invitation accepted! Please set up your password.');
            navigate(redirectPath, {replace: true});

        } catch (error: any) {
            console.error(`Find ${userType} error:`, error);
            toast.error(error.message || `Failed to find ${userType} record`);
            navigate('/login');
        }
    };

    // UPDATE the render to show password setup form for secure token flow:
    if (isSecureTokenFlow && invitationData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                            Set Up Your Password
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Welcome <strong>{invitationData.first_name}</strong>! Please create your password.
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handlePasswordSetup}>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email Address
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={invitationData.email}
                                    disabled
                                    className="mt-1 bg-gray-50"
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    New Password
                                </label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    className="mt-1"
                                    placeholder="Enter your password (minimum 8 characters)"
                                />
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                    Confirm Password
                                </label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    className="mt-1"
                                    placeholder="Confirm your password"
                                />
                            </div>
                        </div>

                        <div>
                            <Button
                                type="submit"
                                disabled={settingPassword}
                                className="w-full"
                            >
                                {settingPassword ? 'Setting Password...' : 'Set Password & Sign In'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // KEEP your existing loading UI for magic link flow:
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full text-center p-6">
                <LoadingSpinner size="lg"/>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                    Processing {userType === 'host' ? 'Host' : 'Admin'} Invitation
                </h2>
                <p className="mt-2 text-gray-600">
                    Please wait while we verify your invitation...
                </p>
                {userEmail && (
                    <p className="mt-2 text-sm text-gray-500">
                        Email: {userEmail}
                    </p>
                )}
            </div>
        </div>
    );
};

export default AcceptInvitation;
